import {addToast, Button, Icon, IconButton, Text} from '@albert-io/atomic';
import classNames from 'classnames';
import React, {useEffect, useRef, useState} from 'react';
import {normalizeError} from 'lib/normalizeError';
import {PlaybackRateMenu, formatPlaybackRate} from 'generic/AudioPlayer/PlaybackRateMenu';

// Pressing play reveals the scrubber + speed menu; pausing holds them for a beat, then fades them
// away, restoring the title. Per design: hold 1000ms, then fade over 400ms.
const CONTROLS_FADE_HOLD_MS = 1000;

/**
 * Compact audio player used for small audio supplements in the item bank and drop slots: a play
 * affordance over the supplement title that, on play, reveals a slim scrubber and a playback-speed
 * toggle. Pausing holds them briefly, then fades them out and restores the title.
 */
export const SmallAudioSupplement = ({
  src,
  caption,
  isOptionEliminated
}: {
  src: string;
  caption: string;
  isOptionEliminated?: boolean;
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const fillRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // `showControls` keeps the scrubber/speed toggle mounted through the fade-out; `controlsFaded`
  // drives their opacity so they linger briefly after pausing before disappearing.
  const [showControls, setShowControls] = useState(false);
  const [controlsFaded, setControlsFaded] = useState(false);

  useEffect(() => {
    if (isOptionEliminated) {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        setIsPlaying(false);
      }
    }
  }, [isOptionEliminated]);

  useEffect(() => {
    if (isPlaying) {
      setShowControls(true);
      setControlsFaded(false);
      return undefined;
    }
    if (!showControls) return undefined;
    const fadeTimer = setTimeout(() => setControlsFaded(true), CONTROLS_FADE_HOLD_MS);
    return () => clearTimeout(fadeTimer);
  }, [isPlaying, showControls]);

  const progress = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
  useEffect(() => {
    if (fillRef.current) {
      fillRef.current.style.width = `${progress}%`;
    }
  }, [progress]);

  const togglePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => {
          const error = normalizeError(err);
          addToast({
            color: 'negative',
            title: 'Error',
            message: error.message
          });
          setIsPlaying(false);
        });
    }
  };

  const selectPlaybackRate = (rate: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.playbackRate = rate;
    setPlaybackRate(rate);
  };

  const seek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const nextTime = parseFloat(e.target.value);
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const handleControlsTransitionEnd = (e: React.TransitionEvent) => {
    if (e.propertyName === 'opacity' && controlsFaded && !isPlaying) {
      setShowControls(false);
    }
  };

  const fadeClassName = classNames('transition-opacity duration-[400ms]', {
    'pointer-events-none opacity-0': controlsFaded
  });
  // Keep interactions with the audio controls (dragging the scrubber, or activating a control by
  // pointer or keyboard) from reaching the surrounding draggable chip, which would otherwise
  // select/drag the whole supplement. The controls stay keyboard- and screen-reader-operable.
  const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <div className='gap-x1 p-x2 relative flex w-full min-w-fit flex-col items-center text-center'>
      <div>
        <Icon icon='file-audio' iconStyle='light' className='size-x4' />
        <audio
          ref={audioRef}
          src={src}
          onEnded={() => {
            setIsPlaying(false);
            setCurrentTime(0);
          }}
          onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
          onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
        >
          <track kind='captions' />
        </audio>
      </div>
      <div className='top-x1 right-x1 gap-x1 absolute flex items-center'>
        {showControls && (
          <PlaybackRateMenu
            playbackRate={playbackRate}
            onSelectRate={selectPlaybackRate}
            position='bottom-start'
            // The chip clips overflow; fixed positioning lets the menu escape that clip.
            positionFixed
            renderTrigger={(triggerProps) => (
              <Button
                {...triggerProps}
                color='secondary'
                variant='outlined'
                roundness='pill'
                size='s'
                aria-label={`Playback speed ${formatPlaybackRate(playbackRate)}`}
                className={classNames(
                  'shrink-0 cursor-pointer! border border-border-primary p-x0-half! text-s shadow-l1 **:cursor-pointer!',
                  fadeClassName
                )}
                onPointerDown={stopPropagation}
                onKeyDown={stopPropagation}
                onClick={(e) => {
                  // Keep the toggle, but don't let the click select/drag the surrounding chip.
                  stopPropagation(e);
                  triggerProps.onClick(e);
                }}
              >
                {formatPlaybackRate(playbackRate)}
              </Button>
            )}
          />
        )}
        <IconButton
          color={isPlaying ? 'primary' : 'secondary'}
          variant={isPlaying ? 'solid' : 'outlined'}
          className={classNames('size-x4! shrink-0 cursor-pointer! shadow-l1 **:cursor-pointer!', {
            'border border-border-primary': !isPlaying
          })}
          onPointerDown={stopPropagation}
          onKeyDown={stopPropagation}
          onClick={togglePlayPause}
          icon={isPlaying ? 'pause' : 'play'}
          label={isPlaying ? 'Pause audio' : 'Play audio'}
          size='s'
        />
      </div>
      {showControls ? (
        <div
          className={classNames(
            'relative flex min-w-full items-center rounded-full focus-within:ring-2 focus-within:ring-border-brand',
            fadeClassName
          )}
          onPointerDown={stopPropagation}
          onMouseDown={stopPropagation}
          onClick={stopPropagation}
          onTransitionEnd={handleControlsTransitionEnd}
        >
          <div className='background-bold flex min-h-[var(--control-target-min-size)] min-w-full overflow-hidden rounded-full'>
            <div ref={fillRef} className='background-brand rounded-full' />
          </div>
          <input
            type='range'
            min='0'
            max={duration || 0}
            value={currentTime}
            step='any'
            aria-label='Seek audio'
            onChange={seek}
            onKeyDown={stopPropagation}
            className='m-x0 absolute inset-0 h-full min-w-full cursor-pointer appearance-none bg-transparent opacity-0'
          />
        </div>
      ) : (
        caption && (
          <Text className='audio-player__title text-s line-clamp-2 min-w-full text-wrap wrap-break-word'>
            {caption}
          </Text>
        )
      )}
    </div>
  );
};

export default SmallAudioSupplement;
